import { createTheme } from "@mui/material/styles";

// Clinical Font Family
export const CLINICAL_FONT_FAMILY =
  '"Inter", "Source Sans Pro", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif';

// Enhanced Clinical Color Palette with Dark Mode Support
export const CLINICAL_COLORS = {
  light: {
    primary: "#0B5FFF",
    secondary: "#0FB6A6",
    background: "#F8FAFC",
    surface: "#FFFFFF",
    surfaceVariant: "#F1F5F9",
    text: {
      primary: "#0F172A",
      secondary: "#64748B",
      disabled: "#9CA3AF",
    },
    border: "#E2E8F0",
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    accent: "#8B5CF6",
  },
  dark: {
    primary: "#4285FF",
    secondary: "#4FC7BA",
    background: "#0F172A",
    surface: "#1E293B",
    surfaceVariant: "#334155",
    text: {
      primary: "#F8FAFC",
      secondary: "#CBD5E1",
      disabled: "#64748B",
    },
    border: "#334155",
    success: "#34D399",
    warning: "#FBBF24",
    error: "#F87171",
    info: "#60A5FA",
    accent: "#A78BFA",
  },
} as const;

// Clinical Font Sizes (matching CSS variables)
export const CLINICAL_FONT_SIZES = {
  xs: "0.75rem", // 12px - Small labels, captions
  sm: "0.875rem", // 14px - Body text, form inputs
  base: "1rem", // 16px - Primary content
  lg: "1.125rem", // 18px - Section headers
  xl: "1.25rem", // 20px - Page headers
  "2xl": "1.5rem", // 24px - Main titles
  "3xl": "1.875rem", // 30px - Hero titles
} as const;

// Clinical Font Weights
export const CLINICAL_FONT_WEIGHTS = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ============================================
// FUTURISTIC UX DESIGN TOKENS
// Enterprise-grade visual polish system
// ============================================

// Elevation System (Material Design Depth)
export const ELEVATION_SHADOWS = {
  0: "0 0 0 0 rgba(0, 0, 0, 0)",
  1: "0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)",
  2: "0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)",
  3: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)",
  4: "0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03)",
} as const;

// Transition Easing
export const TRANSITIONS = {
  fast: "120ms cubic-bezier(0.4, 0, 0.2, 1)",
  base: "180ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "240ms cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

// Border Radius System
export const BORDER_RADIUS = {
  sm: "6px",
  md: "8px",
  lg: "10px",
  xl: "12px",
  full: "9999px",
} as const;

// Glassmorphism Tokens
export const GLASS_MORPHISM = {
  background: "rgba(255, 255, 255, 0.7)",
  border: "rgba(255, 255, 255, 0.18)",
  shadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
  blur: "8px",
} as const;

// CTMS Gradient System
export const CTMS_GRADIENTS = {
  primary: "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
  subtle:
    "linear-gradient(135deg, rgba(11, 95, 255, 0.05) 0%, rgba(15, 182, 166, 0.05) 100%)",
  hover:
    "linear-gradient(135deg, rgba(11, 95, 255, 0.08) 0%, rgba(15, 182, 166, 0.08) 100%)",
} as const;

// Status Badge Colors (with gradients)
export const STATUS_COLORS = {
  pending: {
    bg: "linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)",
    color: "#D97706",
    border: "rgba(245, 158, 11, 0.2)",
  },
  approved: {
    bg: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)",
    color: "#059669",
    border: "rgba(16, 185, 129, 0.2)",
  },
  completed: {
    bg: "linear-gradient(135deg, rgba(11, 95, 255, 0.1) 0%, rgba(15, 182, 166, 0.1) 100%)",
    color: "#0B5FFF",
    border: "rgba(11, 95, 255, 0.2)",
  },
  inProgress: {
    bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)",
    color: "#2563EB",
    border: "rgba(59, 130, 246, 0.2)",
  },
  inactive: {
    bg: "linear-gradient(135deg, rgba(156, 163, 175, 0.1) 0%, rgba(107, 114, 128, 0.1) 100%)",
    color: "#6B7280",
    border: "rgba(156, 163, 175, 0.2)",
  },
} as const;

// Microservice Modules Configuration
export const MICROSERVICE_MODULES = {
  STUDY_MANAGEMENT: {
    name: "Study Management",
    description: "Study creation, configuration, and lifecycle management",
    icon: "Flask",
    color: "#0B5FFF",
    services: ["study-service", "protocol-service", "regulatory-service"],
  },
  SITE_MANAGEMENT: {
    name: "Site Management",
    description: "Clinical site operations and coordination",
    icon: "Building2",
    color: "#0FB6A6",
    services: ["site-service", "staff-service", "facility-service"],
  },
  PARTICIPANT_MANAGEMENT: {
    name: "Participant Management",
    description: "Patient enrollment, screening, and tracking",
    icon: "Users",
    color: "#8B5CF6",
    services: [
      "enrollment-service",
      "screening-service",
      "participant-service",
    ],
  },
  DATA_COLLECTION: {
    name: "Data Collection",
    description: "eCRF, data entry, and validation",
    icon: "FileText",
    color: "#F59E0B",
    services: ["ecrf-service", "data-entry-service", "validation-service"],
  },
  MONITORING: {
    name: "Monitoring",
    description: "Quality assurance and compliance monitoring",
    icon: "Shield",
    color: "#EF4444",
    services: ["monitoring-service", "audit-service", "compliance-service"],
  },
  REPORTING: {
    name: "Reporting & Analytics",
    description: "Data analysis, reports, and insights",
    icon: "BarChart3",
    color: "#10B981",
    services: ["reporting-service", "analytics-service", "export-service"],
  },
} as const;

// PWA Configuration
export const PWA_CONFIG = {
  name: "AIClinTrack",
  shortName: "AIClinTrack",
  description: "AI-Powered Clinical Trial Management System",
  themeColor: "#0B5FFF",
  backgroundColor: "#F8FAFC",
  display: "standalone",
  orientation: "portrait-primary",
  scope: "/",
  startUrl: "/",
  icons: [
    {
      src: "/icons/icon-72x72.png",
      sizes: "72x72",
      type: "image/png",
    },
    {
      src: "/icons/icon-96x96.png",
      sizes: "96x96",
      type: "image/png",
    },
    {
      src: "/icons/icon-128x128.png",
      sizes: "128x128",
      type: "image/png",
    },
    {
      src: "/icons/icon-144x144.png",
      sizes: "144x144",
      type: "image/png",
    },
    {
      src: "/icons/icon-152x152.png",
      sizes: "152x152",
      type: "image/png",
    },
    {
      src: "/icons/icon-192x192.png",
      sizes: "192x192",
      type: "image/png",
    },
    {
      src: "/icons/icon-384x384.png",
      sizes: "384x384",
      type: "image/png",
    },
    {
      src: "/icons/icon-512x512.png",
      sizes: "512x512",
      type: "image/png",
    },
  ],
} as const;

// Responsive Breakpoints
export const BREAKPOINTS = {
  mobile: "320px",
  tablet: "768px",
  desktop: "1024px",
  wide: "1440px",
} as const;

// Material-UI Theme Configuration for Clinical Trials with Dark Mode
export const createClinicalTheme = (mode: "light" | "dark" = "light") => {
  const colors = CLINICAL_COLORS[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: mode === "light" ? "#4285FF" : "#6B9FFF",
        dark: mode === "light" ? "#0047CC" : "#1E40AF",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: colors.secondary,
        light: mode === "light" ? "#4FC7BA" : "#6EE7B7",
        dark: mode === "light" ? "#0A8A7E" : "#047857",
        contrastText: "#FFFFFF",
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
      },
      divider: colors.border,
      success: {
        main: colors.success,
        contrastText: "#FFFFFF",
      },
      warning: {
        main: colors.warning,
        contrastText: "#FFFFFF",
      },
      error: {
        main: colors.error,
        contrastText: "#FFFFFF",
      },
      info: {
        main: colors.info,
        contrastText: "#FFFFFF",
      },
    },
    typography: {
      fontFamily: CLINICAL_FONT_FAMILY,
      h1: {
        fontSize: CLINICAL_FONT_SIZES["2xl"],
        fontWeight: CLINICAL_FONT_WEIGHTS.semibold,
        lineHeight: 1.2,
        letterSpacing: "-0.025em",
      },
      h2: {
        fontSize: CLINICAL_FONT_SIZES.xl,
        fontWeight: CLINICAL_FONT_WEIGHTS.semibold,
        lineHeight: 1.3,
        letterSpacing: "-0.025em",
      },
      h3: {
        fontSize: CLINICAL_FONT_SIZES.lg,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.4,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontSize: CLINICAL_FONT_SIZES.base,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: CLINICAL_FONT_SIZES.sm,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: CLINICAL_FONT_SIZES.xs,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.5,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      },
      subtitle1: {
        fontSize: CLINICAL_FONT_SIZES.base,
        fontWeight: CLINICAL_FONT_WEIGHTS.normal,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: CLINICAL_FONT_SIZES.sm,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: CLINICAL_FONT_SIZES.sm,
        fontWeight: CLINICAL_FONT_WEIGHTS.normal,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: CLINICAL_FONT_SIZES.xs,
        fontWeight: CLINICAL_FONT_WEIGHTS.normal,
        lineHeight: 1.5,
      },
      caption: {
        fontSize: CLINICAL_FONT_SIZES.xs,
        fontWeight: CLINICAL_FONT_WEIGHTS.normal,
        lineHeight: 1.4,
        color: colors.text.secondary,
      },
      overline: {
        fontSize: CLINICAL_FONT_SIZES.xs,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      },
      button: {
        fontSize: CLINICAL_FONT_SIZES.sm,
        fontWeight: CLINICAL_FONT_WEIGHTS.medium,
        letterSpacing: "0.01em",
        textTransform: "none",
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow:
              mode === "light"
                ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)",
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: CLINICAL_FONT_WEIGHTS.medium,
            borderRadius: 8,
            fontFamily: CLINICAL_FONT_FAMILY,
          },
          contained: {
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            boxShadow: `0 2px 4px rgba(11, 95, 255, ${mode === "light" ? "0.2" : "0.4"})`,
            "&:hover": {
              background:
                mode === "light"
                  ? "linear-gradient(135deg, #0047CC 0%, #0A8A7E 100%)"
                  : "linear-gradient(135deg, #6B9FFF 0%, #6EE7B7 100%)",
              boxShadow: `0 4px 8px rgba(11, 95, 255, ${mode === "light" ? "0.3" : "0.5"})`,
            },
          },
          outlined: {
            borderColor: colors.border,
            color: colors.text.primary,
            "&:hover": {
              borderColor: colors.primary,
              backgroundColor:
                mode === "light" ? "#F8FAFC" : colors.surfaceVariant,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              fontFamily: CLINICAL_FONT_FAMILY,
              fontSize: CLINICAL_FONT_SIZES.sm,
              backgroundColor:
                mode === "light" ? colors.surface : colors.surfaceVariant,
            },
            "& .MuiInputLabel-root": {
              fontFamily: CLINICAL_FONT_FAMILY,
              fontSize: CLINICAL_FONT_SIZES.sm,
              fontWeight: CLINICAL_FONT_WEIGHTS.medium,
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: colors.border,
              },
              "&:hover fieldset": {
                borderColor: colors.primary,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.primary,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: CLINICAL_FONT_FAMILY,
            fontSize: CLINICAL_FONT_SIZES.xs,
            fontWeight: CLINICAL_FONT_WEIGHTS.medium,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surface,
            borderColor: colors.border,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            color: colors.text.primary,
            boxShadow:
              mode === "light"
                ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)",
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });
};

// Legacy theme for backward compatibility
export const DASHBOARD_THEME = createClinicalTheme("light");

// Clinical Typography Usage Guidelines
export const CLINICAL_TYPOGRAPHY_GUIDE = {
  "Page Title": {
    variant: "h2",
    description: "Main page titles (20px)",
    example: "Study Overview Dashboard",
  },
  "Section Header": {
    variant: "h3",
    description: "Section headers (18px)",
    example: "Patient Information",
  },
  "Subsection Header": {
    variant: "h4",
    description: "Subsection headers (16px)",
    example: "Vital Signs",
  },
  "Body Text": {
    variant: "body1",
    description: "General content (14px)",
    example: "Clinical documentation text",
  },
  "Caption Text": {
    variant: "caption",
    description: "Helper text, descriptions (12px)",
    example: "Last updated 2 hours ago",
  },
  "Form Labels": {
    variant: "subtitle2",
    description: "Form field labels (14px, medium weight)",
    example: "Patient ID",
  },
  "Category Labels": {
    variant: "overline",
    description: "Category/status labels (12px, uppercase)",
    example: "ACTIVE STUDY",
  },
} as const;

// Helper function to create consistent clinical styling
export const createClinicalSx = (
  variant: keyof typeof CLINICAL_TYPOGRAPHY_GUIDE,
) => {
  void CLINICAL_TYPOGRAPHY_GUIDE[variant];
  return {
    fontFamily: CLINICAL_FONT_FAMILY,
  };
};

// Theme mode management
export const THEME_STORAGE_KEY = "aiclintrack-theme-mode";

export const getStoredThemeMode = (): "light" | "dark" => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
    // Always default to light mode
    return "light";
  }
  return "light";
};

export const setStoredThemeMode = (mode: "light" | "dark") => {
  if (typeof window !== "undefined") {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }
};

// Export the theme creation function as default
export default createClinicalTheme;
